import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DedupApiClient } from 'api/dedup';
import { DedupItemModel } from 'models/Dedup/DedupItem';
import { DedupListingModel } from 'models/Dedup/DedupListing';
import { PaginationModel } from 'models/Pagination/Pagination';
import { useToast } from '@chakra-ui/react';

export const useDedupItems = (artistId?: string) => {
  const cacheKey = artistId && artistId.length > 0 ? artistId : 'all';
  return useQuery<DedupItemModel[]>({
    queryKey: ['dedupItems', cacheKey],
    queryFn: () => DedupApiClient.getDedupItems(artistId),
  });
};

export const useDeleteDedupItem = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (itemId: string) => DedupApiClient.deleteDedupItem(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedupItems'] });
      toast({ title: 'Dedup item removed', status: 'success', duration: 3000, isClosable: true });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to remove dedup item', description: error.message, status: 'error', duration: 4000, isClosable: true });
    },
  });
};

interface DeleteDedupItemPairPayload {
  primaryItemId: string;
  secondaryItemId: string;
}

export const useDeleteDedupItemPair = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ primaryItemId, secondaryItemId }: DeleteDedupItemPairPayload) => DedupApiClient.deleteDedupItemPair(primaryItemId, secondaryItemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedupItems'] });
      toast({ title: 'Dedup match removed', status: 'success', duration: 3000, isClosable: true });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to remove dedup match', description: error.message, status: 'error', duration: 4000, isClosable: true });
    },
  });
};

export const useDedupListings = (page: number) => {
  return useQuery<PaginationModel<DedupListingModel>>({
    queryKey: ['dedupListings', page],
    queryFn: () => DedupApiClient.getDedupListings(page),
  });
};

// Legacy mutation hooks for listing dedup remain below

export const useApproveDedupListing = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => DedupApiClient.approveDedupListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedupListings'] });
      toast({ title: 'Dedup listing approved', status: 'success', duration: 3000, isClosable: true });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to approve dedup listing', description: error.message, status: 'error', duration: 4000, isClosable: true });
    },
  });
};

export const useRejectDedupListing = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: (id: string) => DedupApiClient.rejectDedupListing(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dedupListings'] });
      toast({ title: 'Dedup listing rejected', status: 'success', duration: 3000, isClosable: true });
    },
    onError: (error: Error) => {
      toast({ title: 'Failed to reject dedup listing', description: error.message, status: 'error', duration: 4000, isClosable: true });
    },
  });
};
